import React, { useState, useEffect } from 'react';
import { PEPPER_SHAPES, PEPPER_COLORS } from './constants';

const InstructionsDialog = ({ isOpen, onClose, isMobile = false }) => {
  const [currentTab, setCurrentTab] = useState('instructions');
  const [animationStep, setAnimationStep] = useState(0);

  useEffect(() => {
    if (isOpen) {
      const interval = setInterval(() => {
        setAnimationStep((prev) => (prev + 1) % 4);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const animationBlocks = [
    [1, 0, 0, 0],
    [1, 1, 0, 0],
    [1, 1, 1, 0],
    [0, 0, 0, 0],
  ];

  const mobileInstructions = [
    'Swipe left/right to move',
    'Swipe down to drop faster',
    'Tap to rotate',
    'Match pepper shapes for bonus points',
    'Use color button to change colors',
  ];

  const desktopInstructions = [
    'Use arrow keys or buttons to move and rotate peppers',
    'Fill rows to clear them and score points',
    'Create pepper shapes for big bonus points!',
    'Change colors to match pepper shapes perfectly',
    'Use ALT key to change color quickly',
  ];

  const renderBonusShapes = () => (
    <div className="space-y-4">
      <p className="text-gray-700 mb-4">
        Match these pepper shapes with the same color for bonus points!
        Perfect matches (no extra blocks) give double points.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Object.entries(PEPPER_SHAPES).map(([pepperType, shape]) => (
          <div key={pepperType} className="border rounded-lg p-3 bg-gray-50">
            <h4 className="font-semibold mb-2">{pepperType}</h4>
            <div className="grid grid-cols-5 gap-0.5 w-max mx-auto">
              {shape.map((row, rowIndex) => (
                row.map((cell, colIndex) => (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={`w-6 h-6 ${cell ? PEPPER_COLORS[pepperType] : 'bg-gray-200'} rounded-sm`}
                  />
                ))
              ))}
            </div>
            <p className="text-sm mt-2 text-gray-600">
              Bonus: {100 * shape.flat().filter(Boolean).length} pts
              <br />
              Perfect: {200 * shape.flat().filter(Boolean).length} pts
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-xl transform transition-all max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">How to Play Pepris</h2>
          
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => setCurrentTab('instructions')}
              className={`px-4 py-2 rounded-lg ${
                currentTab === 'instructions'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Controls
            </button>
            <button
              onClick={() => setCurrentTab('bonuses')}
              className={`px-4 py-2 rounded-lg ${
                currentTab === 'bonuses'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Bonus Shapes
            </button>
          </div>

          {currentTab === 'instructions' ? (
            <>
              <ul className="list-disc pl-5 mb-6 space-y-2 text-gray-700">
                {(isMobile ? mobileInstructions : desktopInstructions).map((instruction, index) => (
                  <li key={index} className="text-base">{instruction}</li>
                ))}
              </ul>
              <div className="border-2 border-gray-300 p-2 mb-6 rounded-lg">
                <div className="grid grid-cols-4 gap-1">
                  {animationBlocks[animationStep].map((block, index) => (
                    <div
                      key={index}
                      className={`w-8 h-8 ${
                        block ? 'bg-red-500' : 'bg-gray-200'
                      } transition-colors duration-300 rounded`}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : (
            renderBonusShapes()
          )}

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 active:bg-blue-700 transform active:scale-95 transition-all font-medium text-lg"
            >
              Start Playing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructionsDialog;